import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiRequest, ApiError, AuthRequiredError } from "@/lib/api";

/**
 * Paginated list response shape backend (Phase 2 §03):
 *   `{ items: T[], meta: { next_cursor, prev_cursor, total_estimate } }`
 *
 * I dashboard hooks usavano una forma piatta {data,next_cursor} mai esistita
 * lato BE. Phase 10A: aggiunto adapter `_normalize` per leggere la BE shape e
 * ritornare la forma piatta storica (consumers invariati).
 */
interface PagedRawResponse<T> {
  items: T[];
  meta: {
    next_cursor: string | null;
    prev_cursor: string | null;
    total_estimate?: number | null;
  };
}

interface PagedResponse<T> {
  data: T[];
  next_cursor: string | null;
  prev_cursor: string | null;
  total_estimate?: number;
}

function _normalize<T>(raw: PagedRawResponse<T>): PagedResponse<T> {
  return {
    data: raw.items,
    next_cursor: raw.meta.next_cursor,
    prev_cursor: raw.meta.prev_cursor,
    total_estimate: raw.meta.total_estimate ?? undefined,
  };
}

async function fetchPaged<T>(path: string): Promise<PagedResponse<T>> {
  const raw = await apiRequest<PagedRawResponse<T>>(path);
  return _normalize(raw);
}

/* ────────────────────────────────────────────── Users summary */

export interface UsersSummary {
  total: number;
}

export function useUsersSummary(): UseQueryResult<UsersSummary> {
  return useQuery({
    queryKey: ["dashboard", "users-summary"],
    queryFn: async () => {
      const page = await fetchPaged<unknown>("/users?limit=1");
      return { total: page.total_estimate ?? page.data.length };
    },
    staleTime: 60_000,
  });
}

/* ────────────────────────────────────────────── Packages summary */

export interface PackagesSummary {
  total: number;
  active: number;
  suspended: number;
}

export function usePackagesSummary(): UseQueryResult<PackagesSummary> {
  return useQuery({
    queryKey: ["dashboard", "packages-summary"],
    queryFn: async () => {
      // Strategy: 3 parallel queries with status filter — più affidabile
      // di un endpoint /stats dedicato che non esiste ancora.
      const [all, active, suspended] = await Promise.all([
        fetchPaged<unknown>("/packages?limit=1"),
        fetchPaged<unknown>("/packages?status=active&limit=1"),
        fetchPaged<unknown>("/packages?status=suspended&limit=1"),
      ]);
      return {
        total: all.total_estimate ?? all.data.length,
        active: active.total_estimate ?? active.data.length,
        suspended: suspended.total_estimate ?? suspended.data.length,
      };
    },
    staleTime: 60_000,
  });
}

/* ────────────────────────────────────────────── Pending approvals */

export interface PendingApproval {
  id: string;
  action_type: string;
  target_id: string | null;
  requested_by: string;
  created_at: string;
  expires_at: string | null;
  /** True se l'utente corrente è uno degli approver candidati. */
  for_me?: boolean;
}

export function usePendingApprovals(
  options: { enabled?: boolean; refetchInterval?: number } = {},
): UseQueryResult<{
  data: PendingApproval[];
  total: number;
}> {
  return useQuery({
    ...options,
    queryKey: ["dashboard", "pending-approvals"],
    queryFn: async () => {
      const page = await fetchPaged<PendingApproval>(
        "/approvals?status=pending&limit=10",
      );
      return {
        data: page.data,
        total: page.total_estimate ?? page.data.length,
      };
    },
    staleTime: 30_000,
  });
}

/* ────────────────────────────────────────────── Recent activity (audit) */

export interface AuditEvent {
  id: string;
  occurred_at: string;
  event_type: string;
  category: string;
  outcome: "success" | "failure" | "denied" | "pending";
  severity: "info" | "low" | "medium" | "high" | "critical";
  actor_user_id: string | null;
  target_kind: string | null;
  target_id: string | null;
  summary?: string;
}

export function useRecentActivity(limit = 10): UseQueryResult<AuditEvent[]> {
  return useQuery({
    queryKey: ["dashboard", "recent-activity", limit],
    queryFn: async () => {
      const page = await fetchPaged<AuditEvent>(
        `/audit?limit=${limit}&sort=-occurred_at`,
      );
      return page.data;
    },
    staleTime: 30_000,
  });
}

/* ────────────────────────────────────────────── error helpers */

/**
 * `true` se l'errore è un 404/501/503: l'endpoint backend non è ancora
 * implementato per questo deploy → mostrare placeholder "soon", non error.
 * Esempio: `/audit` non e' ancora montato in Phase 7 (arriva Phase 8H BE).
 */
export function isMissingEndpoint(err: unknown): boolean {
  if (err instanceof AuthRequiredError) return false;
  if (err instanceof ApiError) {
    return err.status === 404 || err.status === 501 || err.status === 503;
  }
  return false;
}
